import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import PageSeo from "@/components/PageSeo";

type Status = "loading" | "valid" | "already" | "invalid" | "success" | "error";

const Unsubscribe = () => {
  const [status, setStatus] = useState<Status>("loading");
  const token = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    const validate = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${token}`,
          { headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY } }
        );
        const data = await res.json();
        if (!res.ok) {
          setStatus("invalid");
        } else if (data.valid === false && data.reason === "already_unsubscribed") {
          setStatus("already");
        } else if (data.valid) {
          setStatus("valid");
        } else {
          setStatus("invalid");
        }
      } catch {
        setStatus("invalid");
      }
    };
    validate();
  }, [token]);

  const handleUnsubscribe = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
        body: { token },
      });
      if (error) throw error;
      if (data?.success) {
        setStatus("success");
      } else if (data?.reason === "already_unsubscribed") {
        setStatus("already");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <PageSeo
        title="E-Mail-Abmeldung – Luther Club"
        description="Abmeldung von E-Mail-Benachrichtigungen des Luther Club Kaiserslautern."
        path="/unsubscribe"
        noindex
      />
      <div className="max-w-md w-full text-center space-y-6">
        <h1 className="text-2xl font-semibold text-primary">E-Mail-Abmeldung</h1>

        {status === "loading" && <p className="text-muted-foreground">Wird überprüft…</p>}

        {status === "valid" && (
          <>
            <p className="text-foreground/80">
              Möchtest du dich wirklich von unseren E-Mails abmelden?
            </p>
            <button
              onClick={handleUnsubscribe}
              className="px-6 py-2 bg-primary text-primary-foreground rounded hover:opacity-90 transition-opacity font-[Montserrat]"
            >
              Abmelden bestätigen
            </button>
          </>
        )}

        {status === "already" && (
          <p className="text-muted-foreground">Du bist bereits abgemeldet.</p>
        )}

        {status === "success" && (
          <p className="text-green-500">Du wurdest erfolgreich abgemeldet.</p>
        )}

        {status === "invalid" && (
          <p className="text-destructive">Ungültiger oder abgelaufener Link.</p>
        )}

        {status === "error" && (
          <p className="text-destructive">Ein Fehler ist aufgetreten. Bitte versuche es später erneut.</p>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
